import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { Avatar, Card, MemoryMark, Switch } from '@wundo/ui';
import { SUPPORT_COLUMN } from './teamColumns.ts';
import { formatNextOneOnOne, formatRelativeDay } from './teamFormat.ts';
import { historyLabel, historyMark } from './colleagueHistory.ts';
import { RelationshipPicker } from './RelationshipPicker.tsx';
import type { ColleagueRelationship, OneOnOneColleagueItem } from '@/lib/colleagues.ts';
import { m } from '@/paraglide/messages.js';

export interface OneOnOneCardProps {
  colleague: OneOnOneColleagueItem;
  onSupportChange: (supported: boolean) => void;
  onRelationshipChange: (relationship: ColleagueRelationship) => void;
}

/**
 * One colleague on the Team page: who, who they are to you, how far Wundo's
 * history of your 1:1s with them goes, when the next one is, and the switch
 * for automatic preparation, titled once in the list's header. The whole card
 * opens their page; the switch and the relationship menu work where they are.
 */
export function OneOnOneCard({
  colleague,
  onSupportChange,
  onRelationshipChange,
}: OneOnOneCardProps) {
  return (
    <Card
      as='li'
      padding='none'
      interactive
      className='relative flex items-center gap-5 px-5 py-3.5 max-mobile:gap-3 max-mobile:px-4 max-mobile:py-3.5'
    >
      <div className='flex min-w-0 flex-1 items-center gap-4 max-mobile:gap-3'>
        <Avatar
          src={colleague.profile_image_url}
          name={colleague.label}
          seed={colleague.email}
          size='md'
        />
        <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
          {/* The name's link stretches over the card; the controls sit above it, since a control inside a link is unreachable. */}
          <Link
            to='/team/$colleagueId'
            params={{ colleagueId: colleague.id }}
            className='truncate text-md font-semibold text-ink outline-none after:absolute after:inset-0 after:rounded-lg focus-visible:after:outline-2 focus-visible:after:outline-ink-faint'
          >
            {colleague.label}
          </Link>
          <RelationshipPicker
            name={colleague.label}
            value={colleague.relationship}
            onChange={onRelationshipChange}
            className='relative z-1'
          />
          <span className='flex items-start gap-1.5 text-sm font-normal text-ink-muted'>
            <MemoryMark state={historyMark(colleague)} className='mt-1' />
            {historyLabel(colleague)}
          </span>
          <span className='text-sm font-normal text-ink-muted mobile:hidden'>
            <When colleague={colleague} inline />
          </span>
        </span>
        <span className='flex w-48 shrink-0 flex-col gap-0.5 max-mobile:hidden'>
          <When colleague={colleague} />
        </span>
      </div>
      {/* Same width as the header's column, so every switch stands under its title. */}
      <span className={`relative z-1 flex items-center ${SUPPORT_COLUMN}`}>
        <Switch
          aria-label={m.team_support_label({ name: colleague.label })}
          checked={colleague.supported}
          onCheckedChange={onSupportChange}
        />
      </span>
      <ChevronRight
        aria-hidden='true'
        className='size-4 shrink-0 text-ink-muted max-mobile:hidden'
      />
    </Card>
  );
}

/**
 * When the next 1:1 is or, with nothing planned, when the last one was. On a
 * desk it is a small block of its own; on a phone one line under the history.
 */
function When({
  colleague,
  inline = false,
}: {
  colleague: OneOnOneColleagueItem;
  inline?: boolean;
}) {
  const next = colleague.next_one_on_one_at;
  const last = colleague.last_one_on_one_at;

  if (next === null && last === null) return null;

  const label = next !== null ? m.team_next_label() : m.team_last_label();
  const when = next !== null ? formatNextOneOnOne(next) : formatRelativeDay(last!);

  if (inline) {
    return (
      <>
        {label}: {when}
      </>
    );
  }

  return (
    <>
      <span className='text-xs font-normal text-ink-muted'>{label}</span>
      <span
        className={`inline-block text-sm first-letter:uppercase ${next !== null ? 'font-semibold text-ink' : 'font-normal text-ink-muted'}`}
      >
        {when}
      </span>
    </>
  );
}
